import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrace } from '../../context/TraceContext';
import { requirements } from '../../data/mockData';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { 
  Scan, FileText, Shield, Copy, ExternalLink, 
  CheckCircle2, Clock, AlertTriangle 
} from 'lucide-react';
import { toast } from 'sonner';

const TraceInspector = () => {
  const navigate = useNavigate();
  const { 
    traceInspectorOpen, 
    closeTraceInspector, 
    currentRouteTrace,
    selectedComponentTrace 
  } = useTrace();

  const copyTraceSummary = () => {
    const summary = `
sQMS PID Trace Summary
======================
Screen: ${currentRouteTrace?.pathname || 'N/A'}

PRD References:
${currentRouteTrace?.prd?.map(p => `  - ${p}`).join('\n') || '  None'}

Requirement IDs:
${currentRouteTrace?.req?.map(r => `  - ${r}`).join('\n') || '  None'}

Notes: ${currentRouteTrace?.notes || 'N/A'}

Generated: ${new Date().toLocaleString('en-IN')}
    `.trim();

    navigator.clipboard.writeText(summary);
    toast.success('Trace summary copied to clipboard');
  };

  const getRequirementDetails = (reqId) => {
    return requirements.find(r => r.id === reqId);
  };

  return (
    <Sheet open={traceInspectorOpen} onOpenChange={closeTraceInspector}>
      <SheetContent className="w-[400px] sm:w-[540px] bg-slate-900 border-slate-800" data-testid="trace-inspector">
        <SheetHeader className="border-b border-slate-800 pb-4">
          <div className="flex items-center gap-2">
            <Scan className="h-5 w-5 text-cyan-400" />
            <SheetTitle className="text-slate-100">Trace Inspector</SheetTitle>
          </div>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-120px)] mt-4">
          <div className="space-y-6 pr-4">
            {/* Screen Info */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Current Screen</h3>
              <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                <p className="text-sm font-mono text-slate-300">{currentRouteTrace?.pathname}</p>
              </div>
            </div>

            {/* PRD References */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">PRD References</h3>
              <div className="space-y-2">
                {currentRouteTrace?.prd?.map((ref, idx) => (
                  <div 
                    key={idx}
                    className="bg-cyan-950/30 rounded-md p-3 border border-cyan-900/50"
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-cyan-400" />
                      <span className="text-sm text-cyan-300 font-medium">{ref}</span>
                    </div>
                    <p className="text-xs text-slate-500 mt-1 ml-6">
                      {ref.includes('Mobile') && 'Mobile execution capabilities with offline support'}
                      {ref.includes('Dashboard') && 'Role-based dashboards with compliance metrics'}
                      {ref.includes('Audit') && 'Audit readiness and evidence management'}
                      {ref.includes('CAPA') && 'Deviation investigation and corrective actions'}
                      {ref.includes('Change') && 'Risk-based change control workflows'}
                      {ref.includes('Requirements') && 'Schedule-M requirement mapping and tracking'}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <Separator className="bg-slate-800" />

            {/* Requirement IDs */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Requirement IDs</h3>
              <div className="space-y-2">
                {currentRouteTrace?.req?.map((reqId, idx) => {
                  const req = getRequirementDetails(reqId);
                  return (
                    <div 
                      key={idx}
                      className="bg-emerald-950/30 rounded-md p-3 border border-emerald-900/50 cursor-pointer hover:border-emerald-700 transition-colors"
                      onClick={() => {
                        closeTraceInspector();
                        navigate(`/requirements?id=${reqId}`);
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Shield className="h-4 w-4 text-emerald-400" />
                          <span className="text-sm text-emerald-300 font-mono">{reqId}</span>
                        </div>
                        {req && (
                          <Badge className={`text-[10px] ${
                            req.coverage >= 90 ? 'bg-emerald-900/50 text-emerald-400' :
                            req.coverage >= 80 ? 'bg-amber-900/50 text-amber-400' :
                            'bg-red-900/50 text-red-400'
                          }`}>
                            {req.coverage}% Coverage
                          </Badge>
                        )}
                      </div>
                      {req && (
                        <>
                          <p className="text-sm text-slate-300 mt-2">{req.title}</p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                            <span>Schedule-M: {req.scheduleMRef}</span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Separator className="bg-slate-800" />

            {/* Evidence Summary */}
            <div>
              <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Evidence Objects</h3>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="flex items-center gap-2 text-slate-400">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    <span className="text-xs">Checklist Executions</span>
                  </div>
                  <p className="text-lg font-bold text-slate-200 mt-1">42</p>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="flex items-center gap-2 text-slate-400">
                    <Clock className="h-4 w-4 text-blue-400" />
                    <span className="text-xs">Audit Trail Events</span>
                  </div>
                  <p className="text-lg font-bold text-slate-200 mt-1">156</p>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="flex items-center gap-2 text-slate-400">
                    <AlertTriangle className="h-4 w-4 text-amber-400" />
                    <span className="text-xs">Deviations Linked</span>
                  </div>
                  <p className="text-lg font-bold text-slate-200 mt-1">3</p>
                </div>
                <div className="bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  <div className="flex items-center gap-2 text-slate-400">
                    <FileText className="h-4 w-4 text-purple-400" />
                    <span className="text-xs">E-Sign Events</span>
                  </div>
                  <p className="text-lg font-bold text-slate-200 mt-1">28</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {currentRouteTrace?.notes && (
              <div>
                <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Notes</h3>
                <p className="text-sm text-slate-400 bg-slate-800/50 rounded-md p-3 border border-slate-700">
                  {currentRouteTrace.notes}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                onClick={copyTraceSummary}
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Summary
              </Button>
              <Button
                className="flex-1 bg-[#fd4816] hover:bg-[#ea380c] text-white"
                onClick={() => {
                  closeTraceInspector();
                  navigate('/requirements');
                }}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Requirements
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default TraceInspector;
